import React, { Component } from 'react';
import { Tween } from 'react-gsap';
import PropTypes from 'prop-types';
import InactivityManager from 'utils/InactivityManager';
import styles from './StartingSound.module.scss';
import PlyrWithClosedCaptions from '@lwtears/lwt-common-frontend/lib/PlayerWithClosedCaptions';
import { captionFormat } from 'utils/caption-util';
import { formatPlyrVolume } from 'utils/plyr-util';
import { getLocale } from '@lwtears/lwt-common-frontend/lib/@common/util/i18n-util';
import { withLwtStore } from '@lwtears/lwt-common-frontend/lib/LwtApp';

const REASON = {
  didNotTouchCard: 'Did not touch the card'
};

const TIMEOUT = process.env.REACT_APP_INACTIVITY_TIMEOUT;

export class StartingSound extends Component {
  _isMounted = false;
  constructor(props) {
    super(props);
    this.state = {
      player: null, // holds the player instance
      canAdvance: false, // Await all playing sounds before allowing next round
      done: false, // End of game
      round: 0 // [-1..2] -1 is introduction round,
    };
    this.inactivityManager = null;
    this.timers = [];
    this.hasAnimation = !!props.currentRound.data.animation;
    this.imageRounds = props.currentRound.data.rounds.length;
    this.maxRounds = this.hasAnimation
      ? props.currentRound.data.rounds.length + 1
      : props.currentRound.data.rounds.length;
  }

  componentDidMount() {
    this._isMounted = true;
    const {
      props: {
        currentRound: {
          audio,
          data: {
            instructions: { introduction }
          }
        }
      }
    } = this;

    const sound = audio[introduction.src];

    if (sound) {
      sound.once('end', () => this.nextRound());
      this.timers.push(setTimeout(() => this._isMounted && sound.play(), 500));
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
    this.props.currentRound.stopAllSounds();
    this.timers.forEach(id => clearTimeout(id));
    this.stopInactivityManager();
  }

  isActivityOver = () => {
    const { done } = this.state;
    return done === true;
  };

  nextRound = () => {
    const {
      props: {
        currentRound: {
          audio,
          data: {
            rounds,
            instructions: { afterRound }
          },
          stopAllSounds
        },
        onRoundEnd
      },
      state: { round, player }
    } = this;

    stopAllSounds();

    if (this.isActivityOver()) return;

    this.inactivityManager && this.stopInactivityManager();

    this._isMounted && this.setState({ canAdvance: false });

    if (rounds[round] && rounds[round].imageAudio) {
      const sound = audio[rounds[round].imageAudio.src];

      if (sound && 'once' in sound) {
        if (round + 1 === this.maxRounds) {
          sound.once('end', () => onRoundEnd());
        }
        if (afterRound[round]) {
          const soundNext = audio[afterRound[round].src];
          soundNext.once('end', () => this.startInactivityManger(soundNext));
          soundNext.once('end', () => this._isMounted && this.setState({ canAdvance: true }));
          sound.once('end', () => this._isMounted && soundNext.play());
        }
        this.timers.push(setTimeout(() => this._isMounted && sound.play(), 500));
      } else {
        this._isMounted && this.setState({ canAdvance: true });
      }
    }

    this._isMounted && this.setState({ round: round + 1, done: round + 1 === this.maxRounds });

    if (round + 1 === this.maxRounds && this.hasAnimation) {
      if (player) this._isMounted && player.play();
    }
  };

  startInactivityManger = sound => {
    const {
      state: { done }
    } = this;

    if (done) {
      this.stopInactivityManager();
      return;
    }

    this.inactivityManager = new InactivityManager(
      TIMEOUT,
      () => this._isMounted && sound.play(),
      REASON.didNotTouchCard
    );
    this.inactivityManager.watch();
  };

  stopInactivityManager = () => {
    if (!this.inactivityManager) return;
    this.inactivityManager.clear();
    this.inactivityManager.stopAudio();
    this.inactivityManager = null;
  };

  handleOnDone = () => {
    this.props.onRoundEnd();
  };

  render() {
    const {
      props: {
        currentRound: {
          data: { animation, rounds, letterCard }
        }
      },
      state: { canAdvance, done, round }
    } = this;

    const {
      accessibility: {
        closedCaptionsSettings,
        audioSettings,
        videoNarrationSettings: { showSettings }
      }
    } = this.props.lwtStoreState.settings;

    const PlyrProps = {
      src: animation.src,
      type: 'video/mp4',
      title: '',
      onDone: this.handleOnDone,
      options: {
        autoplay: true,
        withNativeControls: true,
        locale: getLocale(),
        narrationsAsDefault: showSettings,
        showSettings: showSettings,
        captions: {
          active: closedCaptionsSettings.showClosedCaptions,
          styles: { fontSize: closedCaptionsSettings.closedCaptionSize },
          hideCaptionToggle: true
        }
      },
      config: { volume: formatPlyrVolume(audioSettings.volume, audioSettings.muteSounds) },
      captions: [captionFormat(animation.vtt)],
      narration: {
        src: animation.narrationSrc ?? '',
        captions: [{ src: animation.narrationVtt ?? '', label: '', kind: 'captions' }]
      }
    };

    const isFinalRound = () => animation && round === rounds.length + 1;
    return (
      <div className={styles.container}>
        <div id="starting-sound-flip-card" className={styles.flipcard} data-rounds={this.maxRounds}>
          {!done && (
            <img
              role="button"
              tabIndex="0"
              aria-label={letterCard.description}
              src={letterCard.src}
              alt={''}
              data-active={canAdvance}
              onClick={() => canAdvance && this.nextRound()}
              onKeyDown={ev => {
                ev.key === 'Enter' && canAdvance && this.nextRound();
              }}
            />
          )}
        </div>
        <div className={styles.rounds}>
          {round > 0 && round <= this.imageRounds && (
            <div id="starting-sound-rounds" key={`round-${round}`}>
              <Tween from={{ scale: 0 }} to={{ scale: 1 }} duration={1} ease={'Power3.easeInOut'}>
                <img src={rounds[round - 1].image.src} alt={rounds[round - 1].image.description} />
              </Tween>
            </div>
          )}
          {isFinalRound() && (
            <div
              id="starting-sound-animation"
              key={`round-4`}
              className={
                round === this.maxRounds && this.hasAnimation ? styles.visible : styles.hidden
              }
              style={{ width: '700px' }}
            >
              <PlyrWithClosedCaptions {...PlyrProps} />
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default withLwtStore(StartingSound);

const shapeOfAudio = PropTypes.shape({
  id: PropTypes.number,
  src: PropTypes.string,
  vtt: PropTypes.string,
  narrationSrc: PropTypes.string,
  narrationVtt: PropTypes.string
});

const shapeOfImage = PropTypes.shape({
  id: PropTypes.number,
  src: PropTypes.string,
  description: PropTypes.string
});

StartingSound.propTypes = {
  commonSounds: PropTypes.object,
  currentRound: PropTypes.shape({
    type: PropTypes.string,
    images: PropTypes.array,
    audio: PropTypes.array,
    data: PropTypes.shape({
      instructions: PropTypes.object,
      rounds: PropTypes.arrayOf(
        PropTypes.shape({
          image: shapeOfImage,
          imageAudio: shapeOfAudio
        })
      ),
      animation: shapeOfAudio,
      letterCard: shapeOfImage
    }),
    stopAllSounds: PropTypes.func
  }),
  level: PropTypes.number,
  logError: PropTypes.func,
  onRoundEnd: PropTypes.func,
  lwtStoreState: PropTypes.object
};
